import type { Step } from "@views/Upholstery/types"
import {
  GLOBAL_STYLE_STEP_KEYS,
  type GlobalStyleStepKey,
} from "../steps/fetchUpholsteryStepsV2"
import { isUpholsteryStepsV2Enabled } from "../flags/isUpholsteryStepsV2Enabled"
import { useUpholsteryStepsV2Store } from "../../store/upholstery-steps-v2-store"

/** Local `next dev` only — never log these debuggers in preview/production builds. */
export function isLocalUpholsteryDebug(): boolean {
  return process.env.NODE_ENV === "development"
}

export type StyleStepsSource = "v2" | "per-car"

export type StyleStepsSourceMatch = true | false | "n/a"

export interface StyleStepsSourceDetails {
  flagOn: boolean
  source: StyleStepsSource
  label: "V2" | "OLD (per-car)"
  styleStepIds: Partial<Record<GlobalStyleStepKey, string>>
  matchesStepsV2Store: StyleStepsSourceMatch
}

function collectStyleStepIds(
  steps: Step[]
): Partial<Record<GlobalStyleStepKey, string>> {
  const ids: Partial<Record<GlobalStyleStepKey, string>> = {}
  for (const key of GLOBAL_STYLE_STEP_KEYS) {
    const step = steps.find((s) => s.key === key)
    if (step?.id) {
      ids[key] = step.id
    }
  }
  return ids
}

function doStyleStepIdsMatchStore(
  styleStepIds: Partial<Record<GlobalStyleStepKey, string>>,
  storeSteps: Step[]
): boolean {
  if (storeSteps.length === 0) {
    return false
  }

  return GLOBAL_STYLE_STEP_KEYS.every((key) => {
    const usedId = styleStepIds[key]
    const storeId = storeSteps.find((s) => s.key === key)?.id
    return Boolean(usedId && storeId && usedId === storeId)
  })
}

/**
 * Build a debug summary of where style steps came from.
 * Pass `flagOn` when you already know the flag (e.g. at vehicle load).
 * Otherwise it reads `isUpholsteryStepsV2Enabled()`.
 */
export function getStyleStepsSourceDetails(options: {
  steps: Step[]
  flagOn?: boolean
}): StyleStepsSourceDetails {
  const flagOn = options.flagOn ?? isUpholsteryStepsV2Enabled()
  const source: StyleStepsSource = flagOn ? "v2" : "per-car"
  const label = flagOn ? "V2" : "OLD (per-car)"
  const styleStepIds = collectStyleStepIds(options.steps)
  const storeSteps = useUpholsteryStepsV2Store.getState().steps

  const matchesStepsV2Store: StyleStepsSourceMatch = flagOn
    ? doStyleStepIdsMatchStore(styleStepIds, storeSteps)
    : "n/a"

  return {
    flagOn,
    source,
    label,
    styleStepIds,
    matchesStepsV2Store,
  }
}

/** Computes V2 vs OLD source details. Kept for callers that use the returned value. */
export function logStyleStepsSource(options: {
  steps: Step[]
  flagOn?: boolean
}): StyleStepsSourceDetails | null {
  if (!isLocalUpholsteryDebug()) {
    return null
  }

  return getStyleStepsSourceDetails(options)
}

export type VehicleRecordSource = "dato" | "pdm-only"

/** No-op placeholder kept so callers don't need to change; logging removed for now. */
export function logVehicleRecordSource(_options: {
  source: VehicleRecordSource
  make: string
  model: string
  year: string
}): void {
  // Intentionally silent for now.
}
